import { useState, useRef } from 'react';
import styles from './AppLogo.module.css';
import { useNavigate, useLocation } from 'react-router-dom';

interface GapSenseLogoProps {
  size?: 'sm' | 'md' | 'lg';
  showText?: boolean;
  animated?: boolean;
}

export function GapSenseLogo({
  size = 'md',
  showText = true,
  animated = false
}: GapSenseLogoProps) {
  const [isAnimating, setIsAnimating] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();

  const sizeClass = styles[size];
  const textSizeClass =
    styles[`text${size.charAt(0).toUpperCase() + size.slice(1)}`];

  function handleClick() {
    if (location.pathname === '/') return;

    if (animated) {
      setIsAnimating(true);
      setTimeout(() => setIsAnimating(false), 900);
    }
    navigate('/');
  }

  function handleMouseMove(e: React.MouseEvent) {
    const el = rootRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    el.style.setProperty('--mx', ((x - 0.5) * 2).toFixed(3));
    el.style.setProperty('--my', ((y - 0.5) * 2).toFixed(3));
  }

  function handleMouseLeave() {
    const el = rootRef.current;
    if (!el) return;
    el.style.setProperty('--mx', '0');
    el.style.setProperty('--my', '0');
  }

  return (
    <div
      ref={rootRef}
      className={`${styles.root} ${isAnimating ? styles.animating : ''}`}
      onClick={handleClick}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      tabIndex={0}
    >
      <div className={`${styles.logoContainer} ${sizeClass}`}>
        <svg
          className={styles.svg}
          viewBox="0 0 40 40"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <filter id="shadow" x="-30%" y="-30%" width="160%" height="160%">
              <feDropShadow
                dx="0"
                dy="2"
                stdDeviation="2"
                floodOpacity="0.14"
              />
            </filter>
          </defs>
          <rect
            x="6"
            y="5"
            width="19"
            height="23"
            rx="2"
            fill="#4270F7"
            transform="rotate(-8 15 16)"
            filter="url(#shadow)"
          />
          <rect
            x="13"
            y="9"
            width="19"
            height="23"
            rx="2"
            fill="#06B6D4"
            fillOpacity="0.98"
            stroke="#00000010"
            strokeWidth="0.5"
          />
          <rect
            x="13"
            y="9"
            width="4"
            height="23"
            rx="1"
            fill="#4270F7"
            fillOpacity="0.18"
          />
          <rect
            x="19"
            y="14"
            width="10"
            height="2"
            rx="1"
            fill="#fff"
            fillOpacity="0.8"
          />
          <rect
            x="19"
            y="18"
            width="8"
            height="2"
            rx="1"
            fill="#fff"
            fillOpacity="0.65"
          />
          <rect
            x="19"
            y="22"
            width="10"
            height="2"
            rx="1"
            fill="#fff"
            fillOpacity="0.65"
          />
          <rect
            x="19"
            y="26"
            width="6"
            height="2"
            rx="1"
            fill="#fff"
            fillOpacity="0.45"
          />
        </svg>
      </div>

      {showText && (
        <span className={`${styles.text} ${textSizeClass}`}>
          <span className={styles.primary}>Gap</span>
          <span className={styles.secondary}>
            <span className={styles.highlight}>Sense</span>
          </span>
        </span>
      )}
    </div>
  );
}
